"use strict";
/**
 * ESI banner hydration logic
 * Handles the hydration of different banner types with React
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.hydrateESIBanner = void 0;
const react_1 = __importDefault(require("react"));
const client_1 = require("react-dom/client");
const CitrusBanner_1 = __importDefault(require("../components/CitrusBanner/CitrusBanner"));
const KevelBanner_1 = __importDefault(require("../components/KevelBanner/KevelBanner"));
const CitrusAnalytics_1 = __importDefault(require("../components/CitrusAnalytics/CitrusAnalytics"));
const KevelAnalytics_1 = __importDefault(require("../components/KevelAnalytics/KevelAnalytics"));
const BrandAZSlots_1 = __importDefault(require("../components/BrandAZSlots/BrandAZSlots"));
const ContentBlocks_1 = __importDefault(require("../components/ContentBlocks/ContentBlocks"));
const bannerTypeGuards_1 = require("./bannerTypeGuards");
const domHelpers_1 = require("./domHelpers");
/**
 * Safely parse JSON from a script element with error handling
 *
 * @param scriptElem - The script element containing JSON data
 * @returns Parsed data object, or null if parsing fails
 */
const parseDataSafely = (scriptElem) => {
    if (!scriptElem || !scriptElem.textContent) {
        return null;
    }
    try {
        return JSON.parse(scriptElem.textContent);
    }
    catch (error) {
        console.error("Failed to parse banner data JSON:", error, scriptElem);
        return null;
    }
};
/**
 * Hydrate a single ESI banner element with the appropriate React component.
 *
 * This function:
 * 1. Checks if the banner is already hydrated (prevents duplicates)
 * 2. Finds and parses the banner data
 * 3. Determines the banner type using type guards
 * 4. Hydrates with the appropriate React component
 *
 * @param esiToHydrate - The banner root element to hydrate
 */
const hydrateESIBanner = (esiToHydrate) => {
    // Prevent duplicate hydration
    if (esiToHydrate.hasAttribute("data-hydrated")) {
        return;
    }
    // Find the associated data script
    const bannerDataElem = (0, domHelpers_1.findBannerDataScript)(esiToHydrate);
    if (!bannerDataElem) {
        console.warn("No banner data script found for banner element", esiToHydrate);
        return;
    }
    // Mark as hydrated immediately to prevent race conditions
    esiToHydrate.setAttribute("data-hydrated", "true");
    // Parse the banner data with error handling
    const data = parseDataSafely(bannerDataElem);
    if (!data) {
        console.error("Failed to parse banner data, skipping hydration");
        return;
    }
    // Hydrate with the appropriate component based on data type
    try {
        if ((0, bannerTypeGuards_1.isBrandAZSlotsData)(data)) {
            (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(BrandAZSlots_1.default, { data: data }));
        }
        else if ((0, bannerTypeGuards_1.isContentBlocksData)(data)) {
            (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(ContentBlocks_1.default, { data: data }));
        }
        else if ((0, bannerTypeGuards_1.isKevelBannerData)(data)) {
            (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(KevelBanner_1.default, Object.assign({}, data)));
        }
        else if ((0, bannerTypeGuards_1.isCitrusBannerData)(data)) {
            (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(CitrusBanner_1.default, Object.assign({}, data)));
        }
        else if ((0, bannerTypeGuards_1.isAnalyticsData)(data)) {
            (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(CitrusAnalytics_1.default, null),
                react_1.default.createElement(KevelAnalytics_1.default, null)));
        }
        else {
            console.warn("Unknown banner data type, skipping hydration", data);
        }
    }
    catch (error) {
        console.error("Failed to hydrate banner:", error, data);
    }
};
exports.hydrateESIBanner = hydrateESIBanner;
